import { useThreadSocket } from "@/components/messages/hooks/useThreadSocket.ts";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import { useThrottledInvalidation } from "@/shared/useThrottledInvalidation.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Keep a by-id artifact fetch live for the surfaces that hold their own copy of
 * an artifact — the canvas modal and the standalone artifact route.
 *
 * The message feed refetches changed messages by id, so its artifacts stay
 * current. `artifactQueries.byId` has no such path: it is a one-shot fetch, so
 * without this the card renders the `status` and `actions[]` it was born with
 * indefinitely — still offering Approve on a scheduling kickoff the agent had
 * already sent (BIG-6745).
 *
 * `updateArtifact` bumps the owning message's thread event on every write, so
 * `thread_event:update` is the signal. Pass the artifact's OWN `threadId` (the
 * by-id response stamps it), not the surrounding thread's — a home canvas can
 * show an artifact from any thread.
 *
 * Invalidation is throttled: a single agent turn emits one of these per message
 * it touches, and each one would otherwise cost a refetch of an artifact that
 * mostly didn't change.
 */
export const useArtifactLiveUpdates = (
  artifactId: string | undefined,
  threadId: number | null | undefined,
) => {
  const queryClient = useQueryClient();

  const { trigger } = useThrottledInvalidation(
    useCallback(() => {
      if (!artifactId) return false;
      return void queryClient.invalidateQueries({
        queryKey: artifactQueries.byId(artifactId).queryKey,
      });
    }, [queryClient, artifactId]),
  );

  useThreadSocket(threadId ?? null, "thread_event:update", trigger);
};
